import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { GameManager } from "../../Managers/GameManager";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { Tweens } from "../../Components/Effects/Tweens";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { ModuleManager } from "../../Managers/ModuleManager";
import { FreespinsView } from "../Freespins/FreespinsView";
import { StageController } from "../../Components/StageController";
import { DataManager } from "../../Managers/DataManager";
import { SceneHolderElement } from "../../CustomComponents/SceneHolderElement";
import { WinAnimatorFreespins } from "../../CustomComponents/WinAnimatorFreespins";
import { LayoutContainerElement } from "../../CustomComponents/LayoutContainerElement";
import { LayoutManager } from "../../Managers/LayoutManager";

type IWin = Communication.Game.IWin;

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends LayoutContainerElement {
    public static LayoutManager: LayoutManager;

    private _controller: BaseController;
    private _wasFinished: boolean = false;
    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _paylinesSelector: PaylinesSelector;

    private _symbols: Symbols;
    private _machine: Machine;
    private _noWinContainer: ContainerElement;
    private _symbolsContainer: ContainerElement;

    private _winAnimator: WinAnimatorExtended;

    private _teasing: SwitchElement;
    private _background: SwitchElement;

    private _logo: ImageElement;

    private static readonly GambleInTransitionDuration: number = 30; //in frames
    private _gambleTransition: SwitchElement;
    private _gambleTransitionSceneHolder: SceneHolderElement;

    public static PaylinesIndicator: SwitchElement;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));
    }

    create(node: Element): void {
        super.create(node);

        BaseView.LayoutManager = new LayoutManager();

        if (this.childs != null) {
            for (var key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof PaylinesSelector) {
                    this._paylinesSelector = this.childs[key];
                }

                if (key === "GambleTransition" && this.childs[key] instanceof SwitchElement) {
                    this._gambleTransition = this.childs[key];
                    this._gambleTransitionSceneHolder = <SceneHolderElement>(
                        this._gambleTransition.getChildElementByName("Scene")
                    );
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "SymbolsContainer" && this.childs[key] instanceof ContainerElement) {
                    this._symbolsContainer = this.childs[key];
                }

                if (key === "PaylinesIndicator" && this.childs[key] instanceof SwitchElement) {
                    BaseView.PaylinesIndicator = this.childs[key];
                }

                if (key === "Teasing" && this.childs[key] instanceof SwitchElement) {
                    this._teasing = this.childs[key];
                }

                if (key === "Background" && this.childs[key] instanceof SwitchElement) {
                    this._background = this.childs[key];
                }

                if (key === "Logo" && this.childs[key] instanceof ImageElement) {
                    this._logo = this.childs[key];
                }
            }

            if (this._symbols == null) {
                if (DEBUG) {
                    this.log("ERROR no symbols");
                }
            }

            if (this._machine == null) {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }

            if (this._paylines == null) {
                if (DEBUG) {
                    this.log("WARNING no paylines");
                }
            }

            if (this._winAnimator == null) {
                if (DEBUG) {
                    this.log("WARNING no winAnimator");
                }
            }
        }

        this.setupGambleTransitionSceneElement();
    }

    protected layoutChanged() {}

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.showExpandWilds.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.baseResponse.initData.reels,
                            this.childs["Teasing"],
                            this.childs["Background"],
                        );
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            this._winAnimator.on(
                                WinAnimatorFreespins.ExpandWildAnimFinish,
                                this.expandWildFinished.bind(this),
                            );
                            this._winAnimator.currentWinSymbolsContainer = this.childs["CurrentWinSymbolsContainer"];
                            this._winAnimator.initialize(
                                this._symbols,
                                this._paylines,
                                this._noWinContainer,
                                this._symbolsContainer,
                            );
                            this._winAnimator.initExpandWilds(
                                this.childs["ExpandWilds"],
                                this.childs["ExpandRotation"],
                            );
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                            this._winAnimator.showExpandWilds(GameManager.baseResponse.screen.reels, true);
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        if (BaseView.PaylinesIndicator != null) {
            this.addChild(BaseView.PaylinesIndicator);
            BaseView.PaylinesIndicator.goToCase("EMPTY");
        }

        if (this._gambleTransition != null) {
            this._gambleTransition.goToCase("HIDE_GAMBLE_INIT");
        }
    }

    disableLogo(): void {
        if (this._logo != null) {
            this.removeChild(this._logo);
            this._logo = null;
        }
    }

    initIntro(): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;

        this.visible = true;

        if (this._gambleTransition != null) {
            this._gambleTransition.goToCase("SHOW_INIT");
        }

        if (BaseView.PaylinesIndicator != null) {
            this.addChild(BaseView.PaylinesIndicator);
            BaseView.PaylinesIndicator.goToCase("EMPTY");
        }
        if (GameManager.gameResponse.totalWin > 0) {
            if (ModuleManager.freespinsEnabled()) {
                //init state before fs
                PlayList.loop("SND_FSWait");
                this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
            } else if (GameManager.baseResponse.currentWin == 0) {
                //init state after fs
                PlayList.loop("SND_BaseRecapLoop");
            } else {
                PlayList.loop("SND_BaseRecapLoop");
                this._winAnimator.showInitWinsBonus(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
            }
        } else {
            PlayList.loop("SND_BaseLoop");
            this._winAnimator.showInitWinsBonus(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
        }
    }

    collect(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");

        if (BaseView.PaylinesIndicator != null) {
            this.addChild(BaseView.PaylinesIndicator);
            BaseView.PaylinesIndicator.goToCase("EMPTY");
        }

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }
        this._winAnimator.reset(true);
    }

    spin(): void {
        PlayList.stop("SND_BaseRecapLoop");
        PlayList.loop("SND_BaseLoop");

        this._winAnimator.hideExpandWilds(true);

        if (BaseView.PaylinesIndicator != null) {
            this.addChild(BaseView.PaylinesIndicator);
            BaseView.PaylinesIndicator.goToCase("EMPTY");
        }

        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }
        this._machine.spinStart(this._winAnimator.reset(true), true, this._winAnimator.currentSymbols);
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(
            GameManager.baseResponse.screen.reels,
            this._machine.actualReelStopped,
            this._gambleTransition,
        );
    }

    machineStopped(): void {
        this._wasFinished = false;
        this._controller.machineStopped();
        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
        }
    }

    showExpandWilds(): void {
        this._controller.showExpandWilds();
        this._winAnimator.showExpandWilds(GameManager.baseResponse.screen.reels);
    }

    expandWildFinished(): void {
        this.machineStopped();
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay || ModuleManager.freespinsEnabled()) {
            PlayList.stop("SND_BaseLoop");

            if (ModuleManager.freespinsEnabled()) {
                PlayList.loop("SND_FSWait");
            } else {
                // PlayList.loop('SND_BaseRecapLoop');
            }
        }

        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
    }

    intro(long: boolean = false): void {
        StageController.baseStage = true;
        StageController.freespinStage = false;

        this.allSwitchElementToCase("NORMAL");

        if (GameManager.gameResponse.totalWin > 0) {
            //fs return
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecapLoop");
        } else {
            PlayList.stop("SND_BaseRecapLoop");
            PlayList.loop("SND_BaseLoop");
        }

        if (long) {
            if (FreespinsView.Transition != null) {
                this.addChild(FreespinsView.Transition);
            }
            if (this._gambleTransition != null) {
                this._gambleTransition.setDynamicTime(0);
                this._gambleTransition.goToCase("SHOW");
                if (BaseView.PaylinesIndicator != null) {
                    this.addChild(BaseView.PaylinesIndicator);
                    BaseView.PaylinesIndicator.goToCase("INIT");
                }
            }
            this._introDelay.start(15);
        } else if (this._gambleTransition != null) {
            if (this._gambleTransition != null) {
                this._gambleTransition.setDynamicTime(0);
                this._gambleTransition.goToCase("SHOW");
                if (BaseView.PaylinesIndicator != null) {
                    this.addChild(BaseView.PaylinesIndicator);
                    BaseView.PaylinesIndicator.goToCase("INIT");
                }
            }
            this._introDelay.start(15);
        } else {
            if (this._gambleTransition != null) {
                this._gambleTransition.setDynamicTime(0);
                this._gambleTransition.goToCase("SHOW");
                if (BaseView.PaylinesIndicator != null) {
                    this.addChild(BaseView.PaylinesIndicator);
                    BaseView.PaylinesIndicator.goToCase("INIT");
                }
            }
            this.introFinished();
        }
    }

    introFinished(): void {
        this._controller.introFinished();
    }

    outro(): void {
        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_BaseRecapLoop");

        let wasFinished: boolean = this._wasFinished;
        let inHighWins: boolean = this._winAnimator.reset(true);

        if (ModuleManager.freespinsActive()) {
            if (FreespinsView.Transition != null) {
                this.addChild(FreespinsView.Transition);
                FreespinsView.Transition.goToCase("IN");

                PlayList.stop("SND_FSWait");
                PlayList.play("SND_FSTransIn");
            }

            this._outroDelay.start(15);
        } else {
            if (wasFinished) {
                this.outroStart();
            } else {
                if (inHighWins) {
                    PlayList.play("SND_GambleTransition");
                    this._gambleTransition.setDynamicTime(0);
                    this._gambleTransition.goToCase("HIDE_GAMBLE");
                    this._outroDelay.start(60);
                } else {
                    PlayList.play("SND_GambleTransition");
                    this._gambleTransition.setDynamicTime(0);
                    this._gambleTransition.goToCase("HIDE_GAMBLE");
                    this._outroDelay.start(30);
                }
            }
        }
    }

    outroStart(): void {
        if (ModuleManager.freespinsActive()) {
            StageController.baseStage = false;
            this._winAnimator.hideExpandWilds(false);
            this.allSwitchElementToCase("EMPTY");
            this.outroFinished();
        } else {
            PlayList.play("SND_GambleTransition");
            this._gambleTransition.setDynamicTime(0);
            this._gambleTransition.goToCase("HIDE_GAMBLE");
            this._outroDelay.start(30);
        }
    }

    outroFinished(): void {
        this._controller.outroFinished();
        this._winAnimator.resetTimer();
    }

    selectPayline(): void {
        if (this._paylinesSelector != null) {
            this._paylinesSelector.select();
        }
    }

    private allSwitchElementToCase(caseType: string): void {
        for (let key in this.childs) {
            if (this.childs[key] instanceof SwitchElement) {
                let elem: SwitchElement = this.childs[key];

                if (elem.hasCase(caseType)) {
                    elem.goToCase(caseType);
                }
            }
        }
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
            PlayList.stop("SND_FSLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecapLoop");
        }
    }

    private setupGambleTransitionSceneElement(): void {
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["ReelsFrame"],
            parent: this,
            index: this.getChildIndex(this.childs["ReelsFrame"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Machine"],
            parent: this,
            index: this.getChildIndex(this.childs["Machine"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["ExpandRotation"],
            parent: this,
            index: this.getChildIndex(this.childs["ExpandRotation"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["NoWinContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["NoWinContainer"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["PaylinesIndicator"],
            parent: this,
            index: this.getChildIndex(this.childs["PaylinesIndicator"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Paylines"],
            parent: this,
            index: this.getChildIndex(this.childs["Paylines"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["SymbolsContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["SymbolsContainer"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["CurrentWinSymbolsContainer"],
            parent: this,
            index: this.getChildIndex(this.childs["CurrentWinSymbolsContainer"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["ExpandWilds"],
            parent: this,
            index: this.getChildIndex(this.childs["ExpandWilds"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["Teasing"],
            parent: this,
            index: this.getChildIndex(this.childs["Teasing"]),
        });
        this._gambleTransitionSceneHolder.addElement({
            element: this.childs["WinAnimator"],
            parent: this,
            index: this.getChildIndex(this.childs["WinAnimator"]),
        });
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/